import { memo, useCallback, useMemo } from 'react';

import { type ShortUrlResponse } from '@air/api/types';

import { CommentsRow } from '~/components/ShareModal/components/InfoCommentsSection/CommentsRow';
import { CustomFieldsRow } from '~/components/ShareModal/components/InfoCommentsSection/CustomFieldsRow';
import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { ShareModalSectionLabel } from '~/components/ShareModal/components/ShareModalSectionLabel';
import { ToggleInfoTooltip } from '~/components/ShareModal/components/ToggleInfoTooltip';
import { UpsellFlashIconButton } from '~/components/ShareModal/components/UpsellFlashIconButton';
import { type OnShortUrlUpdate } from '~/components/ShareModal/types/onShortUrlUpdate';
import { type ShareModalUpdateResponse } from '~/components/ShareModal/types/shareModalUpdateResponse';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';
import {
  shareUrlUpdatedCommentsFromBooleans,
  shareUrlUpdatedCustomFieldsFromBooleans,
  shareUrlUpdatedMetadata,
} from '~/utils/shortUrl/shareUrlUpdatedAnalytics';

export type InfoCommentsSectionProps = {
  shortIdInfo: ShortUrlResponse;
  isLoading: boolean;
  settingsDisabled?: boolean;
  onShortUrlUpdate: OnShortUrlUpdate<ShareModalUpdateResponse>;
  canViewCustomFields: boolean;
  canEditCustomFields: boolean;
  canViewDiscussions: boolean;
  canManageDiscussions: boolean;
};

export const InfoCommentsSection = memo(
  ({
    shortIdInfo,
    isLoading,
    settingsDisabled = false,
    onShortUrlUpdate,
    canViewCustomFields,
    canEditCustomFields,
    canViewDiscussions,
    canManageDiscussions,
  }: InfoCommentsSectionProps) => {
    const { metadataEnabled } = useShareSettings();

    const showMetadata = useMemo(
      () => shortIdInfo.presentationSettings?.hideInfoTab !== true,
      [shortIdInfo.presentationSettings?.hideInfoTab],
    );

    // Hiding info also revokes customField visibility so public surfaces (which gate on
    // `customField.canView`) hide custom metadata downstream — same combined-write as V1.
    const handleToggleMetadataVisibility = useCallback(
      (checked: boolean) => {
        if (checked) {
          void onShortUrlUpdate({
            props: { presentationSettings: { hideInfoTab: false } },
            analytics: shareUrlUpdatedMetadata(true),
          });
          return;
        }
        void onShortUrlUpdate({
          props: {
            presentationSettings: { hideInfoTab: true },
            permissions: { customField: { canView: false, canManageApplications: false } },
          },
          analytics: shareUrlUpdatedMetadata(false),
        });
      },
      [onShortUrlUpdate],
    );

    const metadataLabelAccessory = useMemo(
      () =>
        metadataEnabled ? (
          <ToggleInfoTooltip text="Show file info and custom metadata to public viewers" />
        ) : (
          <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>
        ),
      [metadataEnabled],
    );

    const handleCustomFieldsChange = useCallback(
      (next: { canView: boolean; canManageApplications: boolean }) =>
        onShortUrlUpdate({
          props: { permissions: { customField: next } },
          analytics: shareUrlUpdatedCustomFieldsFromBooleans(next.canView, next.canManageApplications),
        }),
      [onShortUrlUpdate],
    );

    const handleCommentsChange = useCallback(
      (next: { canView: boolean; canManage: boolean }) =>
        onShortUrlUpdate({
          props: {
            permissions: { asset: { canViewDiscussions: next.canView, canManageDiscussions: next.canManage } },
          },
          analytics: shareUrlUpdatedCommentsFromBooleans(next.canView, next.canManage),
        }),
      [onShortUrlUpdate],
    );

    return (
      <div className="flex flex-col gap-2">
        <ShareModalSectionLabel title="Info & comments" />
        <OnOffShareToggleRow
          label="File info & custom metadata"
          labelAccessory={metadataLabelAccessory}
          checked={showMetadata}
          isLoading={isLoading}
          disabled={settingsDisabled || !metadataEnabled}
          onToggle={handleToggleMetadataVisibility}
          data-testid="METADATA_VISIBILITY_TOGGLE"
        />
        <CustomFieldsRow
          canEditCustomFields={canEditCustomFields}
          canManageApplications={shortIdInfo.permissions.customField.canManageApplications}
          canView={shortIdInfo.permissions.customField.canView}
          canViewCustomFields={canViewCustomFields}
          data-testid="CUSTOM_FIELDS_SEGMENTED_CONTROL"
          disabled={settingsDisabled}
          isLoading={isLoading}
          onChange={handleCustomFieldsChange}
          showMetadata={showMetadata}
        />
        <CommentsRow
          canManage={shortIdInfo.permissions.asset.canManageDiscussions}
          canManageDiscussions={canManageDiscussions}
          canView={shortIdInfo.permissions.asset.canViewDiscussions}
          canViewDiscussions={canViewDiscussions}
          data-testid="COMMENTS_SEGMENTED_CONTROL"
          disabled={settingsDisabled}
          isLoading={isLoading}
          onChange={handleCommentsChange}
        />
      </div>
    );
  },
);

InfoCommentsSection.displayName = 'InfoCommentsSection';
